<template>
    <flex-element
        :element-type="elementType"
        class="f-inline-block f-self-start f-cursor-hover"
        class-default-inline="f-border-1 f-px-0.5 f-py-0.25 f-bg-white"
    >
        <select
            v-model="selected"
            class="f-w-1/1 f-h-1/1 f-bg-transparent"
            :name="inputNameNew"
            :required="renderRequired"
            :style="editorStyles"
            :aria-label="ariaLabelNew"
            :aria-labelledby="ariaLabelledByNew"
            @input="onInput"
            @invalid="onInvalidInput"
            @change="changeEvent"
        >
            <option
                v-if="renderPlaceholder"
                value=""
                selected
            >
                {{ renderPlaceholder }}
            </option>

            <option
                v-for="(value, key) in computedOptions"
                :key="value"
                :value="value"
            >
                {{ key }}
            </option>
        </select>
    </flex-element>
</template>

<script>
import jsonpath from 'jsonpath';
import { mapState, mapMutations } from 'vuex';

import baseAtom from '@/mixins/base-atom/base-atom-smart';
import { ASelect } from '@/data/data-models';
import { FIRE_FLEX_EVENT } from '@/store/types/mutation-types';
import selectDataQuery from '@/graphql/queries/SelectData.gql';

const publicMode = process.env.VUE_APP_ENV === 'public';

export default {
    name: 'a-select',
    mixins: [baseAtom],

    apollo: {
        itemData: {
            query: selectDataQuery,

            variables() {
                return {
                    dataSourceId: this.datasourceValue,
                };
            },

            update(data) {
                if (!data || !data.item) return [];

                return data.item.children;
            },

            skip() {
                return !this.datasourceValue;
            },
        },
    },

    props: {
        displayName: {
            type: String,
            default: 'Dropdown',
        },

        inputName: {
            type: String,
            default: '',
        },

        options: {
            type: Object,
            default: () => ({}),
        },

        placeholder: {
            type: String,
            default: '',
        },

        placeholderDefault: {
            type: String,
            default: 'Select One',
        },

        required: {
            type: Boolean,
            default: false,
        },

        requiredDefault: {
            type: Boolean,
            default: false,
        },

        validationMessage: {
            type: String,
            default: '',
        },
    },

    data() {
        return {
            cssDisplay: 'inline-block',
            inputNameNew: '',
            placeholderNew: '',
            requiredNew: '',
            optionsNew: {},
            validationMessageNew: '',
            selected: '',
        };
    },

    computed: {
        ...mapState({
            previewMode: state => state.previewMode,
        }),

        computedOptions() {
            if (this.itemData && this.itemData.length > 0) {
                const namePath = this.datasourceTextPath || '$.name';
                const valuePath = this.datasourceValuePath || '$.fields[0].value';
                return Object.assign({}, ...this.itemData.map(object => ({ [jsonpath.query(object, namePath)[0]]: jsonpath.query(object, valuePath)[0] })));
            }
            return this.options;
        },

        datasourceValue() {
            return this.getElementData('selectDatasource');
        },

        datasourceTextPath() {
            return this.getElementData('selectTextPath');
        },

        datasourceValuePath() {
            return this.getElementData('selectValuePath');
        },

        editorStyles() {
            if (this.previewMode || publicMode) return;

            return {
                'pointer-events': 'none',
            };
        },

        renderPlaceholder() {
            return this.placeholderNew || this.placeholderDefault;
        },

        renderRequired() {
            return this.requiredNew || this.requiredDefault;
        },

        updatedElement() {
            return new ASelect({
                ...this.updatedBaseElement,
                inputName: this.inputNameNew,
                options: this.optionsNew,
                required: this.requiredNew,
                validationMessage: this.validationMessageNew,
                placeholder: this.placeholderNew,
            });
        },
    },

    watch: {
        computedOptions() {
            if (this.datasourceValue) {
                this.preselectData();
            }
        },
    },

    mounted() {
        if (!this.datasourceValue) {
            this.preselectData();
        }
    },

    methods: {
        ...mapMutations({
            fireFlexEvent: FIRE_FLEX_EVENT,
        }),

        preselectData() {
            const urlParams = new URLSearchParams(window.location.search);
            urlParams.forEach((value, key) => {
                if (this.inputNameNew === key) {
                    const splitValues = value.split('|');
                    splitValues.forEach((sv) => {
                        if (Object.values(this.computedOptions).filter(ov => ov === sv).length > 0) {
                            this.selected = sv;
                        }
                    });
                }
            });
        },

        setFieldValues() {
            this.inputNameNew = this.inputName;
            this.requiredNew = this.required;
            this.validationMessageNew = this.validationMessage;
            this.optionsNew = this.options;
            this.placeholderNew = this.placeholder || this.placeholderDefault;
        },

        onInput(e) {
            e.target.setCustomValidity('');
        },

        onInvalidInput(e) {
            e.target.setCustomValidity(this.validationMessageNew);
        },

        changeEvent(e) {
            this.fireFlexEvent(`change-${ this.inputNameNew }`);
            this.fireFlexEvent(`change-${ this.inputNameNew }-${ e.target.value }`);
            if (this.getElementData('selectAutoSubmit') === 'true') {
                this.$emit('submit-form');
            }
        },
    },
};
</script>
